import moment from "moment-timezone";
import { formatLocalDateTime, parseUtcInstant } from "./dateTimeUtils";
import { mergeChartZoomOptions } from "./chartZoomPlugin";
import { formatWaterLevelTooltipCm } from "./waterLevelFormatUtils";

const MIN_AXIS_SPAN_MS = 45 * 60 * 1000; // 45 min minimum window
const PADDING_RATIO = 0.08;

/** Ceil cm-axis max to this step so small value drift does not rescale charts. */
export const STABLE_CM_AXIS_STEP_CM = 25;

/**
 * Stable Y max (cm): ceil(maxValue / step) * step.
 * e.g. 38 → 50, 459 → 475, 575 → 600.
 */
export function resolveStableCmAxisMax(
  values,
  step = STABLE_CM_AXIS_STEP_CM,
) {
  let rawMax = null;
  for (const value of values ?? []) {
    if (value == null || value === "") continue;
    const num = Number(value);
    if (!Number.isFinite(num)) continue;
    rawMax = rawMax == null ? num : Math.max(rawMax, num);
  }
  if (rawMax == null || rawMax <= 0) return null;
  const size = step > 0 ? step : STABLE_CM_AXIS_STEP_CM;
  return Math.max(size, Math.ceil(rawMax / size) * size);
}

function nowMs() {
  return Date.now();
}

/** X-axis starts at the first data point; small padding only after the last point. */
function padAxisBounds(dataMin, dataMax) {
  const now = nowMs();
  const max = Math.min(dataMax, now);
  const min = Math.min(dataMin, max);

  if (max <= min) {
    return {
      min,
      max: Math.min(now, min + MIN_AXIS_SPAN_MS),
      spanMs: MIN_AXIS_SPAN_MS,
    };
  }

  let spanMs = max - min;

  if (spanMs < MIN_AXIS_SPAN_MS) {
    return {
      min,
      max: Math.min(now, min + MIN_AXIS_SPAN_MS),
      spanMs: MIN_AXIS_SPAN_MS,
    };
  }

  const rightPad = Math.max(spanMs * PADDING_RATIO, 60 * 1000);
  const paddedMax = Math.min(max + rightPad, now);

  return { min, max: paddedMax, spanMs: paddedMax - min };
}

/**
 * X-axis follows actual data range (first point → last point).
 * Filter range is only used when there are no points to plot.
 */
export function resolveChartAxisBounds(dateRange, timeValuesMs = []) {
  const now = nowMs();
  const valid = timeValuesMs.filter(
    (t) => t != null && Number.isFinite(t) && t <= now,
  );

  if (valid.length > 0) {
    return padAxisBounds(Math.min(...valid), Math.max(...valid));
  }

  const hasFilterRange =
    dateRange?.startMs != null &&
    dateRange?.endMsExclusive != null &&
    dateRange.endMsExclusive > dateRange.startMs;

  if (hasFilterRange) {
    const filterMax = Math.min(dateRange.endMsExclusive, now);
    const filterMin = Math.min(dateRange.startMs, filterMax);
    return {
      min: filterMin,
      max: filterMax,
      spanMs: Math.max(filterMax - filterMin, 0),
    };
  }

  return { min: undefined, max: undefined, spanMs: 0 };
}

/** When a date filter is active, span the axis across the full filter window (e.g. all of today). */
export function resolveChartAxisBoundsForFilter(dateRange, timeValuesMs = []) {
  const now = nowMs();
  const valid = timeValuesMs.filter(
    (t) => t != null && Number.isFinite(t) && t <= now,
  );

  const hasFilter =
    dateRange?.startMs != null &&
    dateRange?.endMsExclusive != null &&
    dateRange.endMsExclusive > dateRange.startMs;

  if (!hasFilter) {
    return resolveChartAxisBounds(null, timeValuesMs);
  }

  const filterMin = dateRange.startMs;
  const filterMax = Math.min(dateRange.endMsExclusive, now);

  if (!valid.length) {
    const spanMs = Math.max(filterMax - filterMin, MIN_AXIS_SPAN_MS);
    return {
      min: filterMin,
      max: Math.max(filterMin + spanMs, filterMax),
      spanMs,
    };
  }

  const dataMin = Math.min(...valid);
  const dataMax = Math.max(...valid);
  return padAxisBounds(
    Math.min(filterMin, dataMin),
    Math.max(filterMax, dataMax),
  );
}

/** Keep every reading; only collapse rows that share the exact same timestamp. */
export function aggregateChartPointsByTime(points) {
  if (!points?.length) return [];

  const now = nowMs();
  const filtered = points
    .filter(
      (point) =>
        point.timeMs != null &&
        point.value != null &&
        Number.isFinite(point.timeMs) &&
        point.timeMs <= now,
    )
    .sort((a, b) => a.timeMs - b.timeMs);

  if (!filtered.length) return [];

  const buckets = new Map();
  for (const point of filtered) {
    const existing = buckets.get(point.timeMs);
    if (!existing) {
      buckets.set(point.timeMs, {
        timeMs: point.timeMs,
        value: point.value,
        count: 1,
      });
      continue;
    }
    existing.value =
      (existing.value * existing.count + point.value) / (existing.count + 1);
    existing.count += 1;
  }

  return Array.from(buckets.values()).sort((a, b) => a.timeMs - b.timeMs);
}

function resolvePointStyle() {
  return {
    radius: 0,
    hoverRadius: 5,
    hitRadius: 12,
  };
}

/** Tick format for chart axis — epoch ms in client local timezone. */
export function formatTelemetryChartAxisTick(timeMs, spanMs) {
  const m = parseUtcInstant(timeMs);
  if (!m) return "";
  const local = m.local();

  if (spanMs <= 2 * 60 * 60 * 1000) {
    return local.format("hh:mm A");
  }
  if (spanMs <= 2 * 24 * 60 * 60 * 1000) {
    return local.format("DD MMM hh:mm A");
  }
  if (spanMs <= 14 * 24 * 60 * 60 * 1000) {
    return local.format("DD MMM");
  }
  return local.format("DD MMM YYYY");
}

export function buildTelemetryTimeScaleOptions({
  yTitle,
  axis,
  xTitle,
  valueTooltipLabel,
  pointCount = 0,
  yMin,
  yMax,
}) {
  const spanMs = axis?.spanMs ?? 0;
  const sparse = pointCount <= 25;
  const pointStyle = resolvePointStyle();
  const axisMax = axis?.max ?? nowMs();
  const hasFixedYMin = yMin != null && Number.isFinite(Number(yMin));
  const hasFixedYMax = yMax != null && Number.isFinite(Number(yMax));

  return mergeChartZoomOptions({
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
      layout: {
        padding: { left: 4, right: 8, top: 4, bottom: 0 },
      },
      plugins: {
      legend: { display: false },
      tooltip: {
        mode: "nearest",
        intersect: false,
        callbacks: {
          title(items) {
            const x = items[0]?.parsed?.x;
            return formatLocalDateTime(x, { fallback: "—" });
          },
          label(context) {
            const value = context.parsed?.y;
            if (value == null || Number.isNaN(value)) {
              return `${valueTooltipLabel}: —`;
            }
            const labelLower = String(valueTooltipLabel ?? "").toLowerCase();
            if (
              labelLower.includes("water level") ||
              labelLower.includes("confirmed level") ||
              (labelLower.includes("level") && labelLower.includes("cm"))
            ) {
              return `${valueTooltipLabel}: ${formatWaterLevelTooltipCm(value)}`;
            }
            const formatted = Number.isInteger(value)
              ? value
              : value.toFixed(2);
            return `${valueTooltipLabel}: ${formatted}`;
          },
        },
      },
      decimation: {
        enabled: false,
      },
    },
    scales: {
      x: {
        type: "linear",
        min: axis?.min,
        max: axisMax,
        offset: false,
        grace: 0,
        bounds: "ticks",
        title: {
          display: true,
          text: xTitle || "Time",
          font: { size: 11, weight: "600" },
          color: "#6b7280",
        },
        grid: { color: "#f3f4f6" },
        ticks: {
          font: { size: 10 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: sparse ? 6 : 8,
          callback: (value) => {
            if (value > axisMax) return "";
            return formatTelemetryChartAxisTick(value, spanMs);
          },
        },
      },
      y: {
        title: {
          display: true,
          text: yTitle,
          font: { size: 11, weight: "600" },
          color: "#6b7280",
        },
        grid: { color: "#f3f4f6" },
        beginAtZero: !hasFixedYMin,
        ...(hasFixedYMin ? { min: Number(yMin) } : {}),
        ...(hasFixedYMax ? { max: Number(yMax) } : {}),
      },
    },
    elements: {
      point: {
        radius: pointStyle.radius,
        hoverRadius: pointStyle.hoverRadius,
        hitRadius: pointStyle.hitRadius,
      },
      line: {
        tension: sparse ? 0.15 : 0.25,
        borderWidth: sparse ? 2 : 1.5,
      },
    },
  });
}

/** Strip chart metadata before passing to react-chartjs-2. */
export function toLineChartJsData(chartData) {
  if (!chartData?.datasets) return { datasets: [] };
  return { datasets: chartData.datasets };
}

/** Build {x: timeMs, y: value} series scaled to data time range. */
export function buildTimeSeriesChartData(
  points,
  { label, color, yTitle, dateRange, xTitle, valueTooltipLabel },
) {
  const aggregated = aggregateChartPointsByTime(points);
  if (!aggregated.length) return null;

  const timeValues = aggregated.map((point) => point.timeMs);
  const axis = resolveChartAxisBoundsForFilter(dateRange, timeValues);
  const pointStyle = resolvePointStyle();
  const sparse = aggregated.length <= 25;
  const dense = aggregated.length > 80;

  return {
    datasets: [
      {
        label,
        data: aggregated.map(({ timeMs, value }) => ({ x: timeMs, y: value })),
        borderColor: color,
        backgroundColor: dense ? `${color}14` : `${color}22`,
        fill: !dense,
        tension: sparse ? 0.15 : dense ? 0.08 : 0.25,
        pointRadius: pointStyle.radius,
        pointHoverRadius: pointStyle.hoverRadius,
        pointHitRadius: pointStyle.hitRadius,
        borderWidth: sparse ? 2 : dense ? 1.25 : 1.5,
        spanGaps: true,
      },
    ],
    axis,
    yTitle,
    xTitle,
    valueTooltipLabel,
    pointCount: aggregated.length,
  };
}
